import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useGoogleAuth } from './useGoogleAuth';
import { useAuthReady, useAuthError, useUser } from '@/app/store';

interface AuthGateProps {
  children: ReactNode;
}

/* =====================================================================
   AuthGate — sólo permite acceso si hay un user de Google.
   Rutas exentas: /login y /admin/login (gestionan su propio flujo).
===================================================================== */

const PUBLIC_PATHS = ['/', '/login', '/admin/login', '/leaderboard'];

const ERROR_HINTS: Record<string, string> = {
  'auth/popup-closed-by-user':
    'Cerraste la ventana antes de completar el login. Intenta de nuevo.',
  'auth/popup-blocked':
    'El navegador bloqueó el popup. Permite popups para este sitio y vuelve a intentar.',
  'auth/network-request-failed':
    'Sin conexión a Firebase. Verifica tu red.',
  'auth/operation-not-allowed':
    'Google Sign-In no está habilitado en Firebase Console.',
  'auth/unauthorized-domain':
    'Este dominio no está autorizado en Firebase Console → Authentication → Settings → Authorized domains.',
};

export function AuthGate({ children }: AuthGateProps) {
  useGoogleAuth();
  const ready = useAuthReady();
  const user = useUser();
  const error = useAuthError();
  const location = useLocation();

  const isPublic = PUBLIC_PATHS.includes(location.pathname);

  if (!ready) {
    return (
      <main
        style={{
          minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#aeb6e6', fontFamily: 'Chakra Petch', letterSpacing: 4,
        }}
      >
        Conectando...
      </main>
    );
  }

  if (error && !isPublic) {
    const hint = ERROR_HINTS[error] ?? 'Error de autenticación.';
    return (
      <main
        style={{
          minHeight: '100vh', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center',
        }}
      >
        <p style={{ color: '#ff5d73', fontFamily: 'Chakra Petch', letterSpacing: 4, marginBottom: 12 }}>
          ERROR DE AUTH
        </p>
        <code style={{ color: '#eef1ff', fontSize: 13, marginBottom: 12 }}>{error}</code>
        <p style={{ color: '#aeb6e6', maxWidth: 480 }}>{hint}</p>
      </main>
    );
  }

  if (!user && !isPublic) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
